import { useState } from "react";
import { Alert, Badge, Button, Card, Grid, Group, Stack, TagsInput, Text, Title } from "@mantine/core";
import { IconReplace } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { KitchenToolRequest } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { AiContextFields } from "../components/AiContextFields";
import { MediaInput, type MediaInputValue } from "../components/MediaInput";
import { BulletSection, KitchenPending, PreferencesFields } from "../components/kitchen";
import { composeMessage } from "../lib/clientSettings";
import { buildPreferences, kitchenRequest, type PreferencesForm } from "../lib/kitchen";

const EXAMPLES: { ingredients: string[]; text: string }[] = [
  { ingredients: ["eggs"], text: "Fudgy vegan brownies" },
  { ingredients: ["buttermilk"], text: "Sunday pancakes, I'm out of buttermilk" },
  { ingredients: ["heavy cream", "parmesan"], text: "Dairy-free creamy pasta sauce" },
];

export function SubstitutesPage() {
  const [input, setInput] = useState<MediaInputValue>({ text: "", media: null });
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [prefs, setPrefs] = useState<PreferencesForm>({});
  const [note, setNote] = useState("");
  const [last, setLast] = useState<KitchenToolRequest | null>(null);
  const m = useMutation({ mutationFn: (req: KitchenToolRequest) => passio.recipes.substitutes(req) });

  const submit = (ings = ingredients, text = input.text) => {
    if (!ings.length && !text.trim() && !input.media) return;
    setIngredients(ings);
    setInput({ ...input, text });
    const req = kitchenRequest({ text, media: input.media, ingredients: ings, message: composeMessage(note), preferences: buildPreferences(prefs) });
    setLast(req);
    m.mutate(req);
  };
  const data = m.data?.data;

  return (
    <RequireFeature feature="cooking">
      <PageHeader
        eyebrow="POST /napi/tools/recipes/substitutes"
        title="Ingredient substitutes"
        description="Ranked swaps for one or more ingredients, with exact ratios and method, how taste and texture change, and which diets each fits. Context (the dish) changes the answer, so send it."
        actions={<RouteModelPicker routes={["recipesSubstitutes"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <TagsInput label="Ingredients to replace" placeholder="Type and press Enter" value={ingredients} onChange={setIngredients} />
              <MediaInput value={input} onChange={setInput} kinds={["image", "audio"]} textPlaceholder="What are you making? e.g. vegan brownies" photoLabel="Drop a photo of the recipe or the label" />
              <PreferencesFields value={prefs} onChange={setPrefs} fields={["diet", "allergies", "dislikes"]} />
              <AiContextFields note={note} onNoteChange={setNote} placeholder="e.g. I only have pantry staples" />
              <Button leftSection={<IconReplace size={16} />} onClick={() => submit()} loading={m.isPending} disabled={!ingredients.length && !input.text.trim() && !input.media} size="md">
                Find substitutes
              </Button>
              <Stack gap={6}>
                <Text fz="xs" c="dimmed">
                  Examples
                </Text>
                {EXAMPLES.map((ex) => (
                  <Text key={ex.text} fz="sm" c="violet.7" style={{ cursor: "pointer" }} onClick={() => submit(ex.ingredients, ex.text)}>
                    {ex.ingredients.join(" + ")} in “{ex.text}”
                  </Text>
                ))}
              </Stack>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => last && m.mutate(last)} />}
          {m.isPending && <KitchenPending title="Finding swaps…" />}
          {data && !m.isPending && (
            <Stack gap="md">
              <ResultHeader title="Substitutes" count={data.items.length} meta={m.data?.meta} feedback={<FeedbackButtons requestId={m.data?.meta.requestId} />} />
              {data.notes && (
                <Alert color="orange" variant="light">
                  <Text fz="sm">{data.notes}</Text>
                </Alert>
              )}
              {data.items.length === 0 && <EmptyState title="Nothing to replace">Name the ingredient you want to swap.</EmptyState>}
              {data.items.map((item, i) => (
                <Card key={i}>
                  <Group gap="xs" mb={4}>
                    <Title order={3} fz={18}>
                      {item.ingredient}
                    </Title>
                    {item.context && <Badge variant="outline" color="gray" tt="none">in {item.context}</Badge>}
                  </Group>
                  {item.reason && item.reason !== "not stated" && (
                    <Text fz="sm" c="dimmed" mb="sm">
                      {item.reason}
                    </Text>
                  )}
                  <Stack gap="sm">
                    {item.substitutes.map((s) => (
                      <Group key={s.rank + s.name} gap="sm" wrap="nowrap" align="flex-start">
                        <Badge size="lg" circle color={s.rank === 1 ? "lime.8" : "ink.8"} variant="filled" style={{ flex: "none" }}>
                          {s.rank}
                        </Badge>
                        <Stack gap={3} style={{ flex: 1 }}>
                          <Group gap={6}>
                            <Text fw={650}>{s.name}</Text>
                            {s.dietFlags.map((f) => (
                              <Badge key={f} size="xs" variant="light" color="lime.8">
                                {f}
                              </Badge>
                            ))}
                          </Group>
                          {s.ratio && (
                            <Text fz="sm" fw={600} c="violet.8">
                              {s.ratio}
                            </Text>
                          )}
                          {s.bestFor && <Text fz="sm">Best for: {s.bestFor}</Text>}
                          {s.tasteTextureImpact && (
                            <Text fz="xs" c="dimmed">
                              {s.tasteTextureImpact}
                            </Text>
                          )}
                          {s.nutritionNote && (
                            <Text fz="xs" c="dimmed" fs="italic">
                              {s.nutritionNote}
                            </Text>
                          )}
                        </Stack>
                      </Group>
                    ))}
                    <BulletSection title="Don't use" items={item.avoid} color="red" />
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
          {!data && !m.isPending && !m.error && <EmptyState title="Out of something?">Add the ingredient and the dish. Allergies in preferences are never suggested.</EmptyState>}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
